import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card, CardHeader, Input, Select, Button, EmptyState, Field,
} from '../../components/ui/index.js';
import { PaymentsTable, PAYMENT_STATUS_OPTIONS as STATUSES } from '../../components/PaymentsTable.jsx';
import { useResource } from '../../hooks/useApi.js';
import { toMinorUnits } from '../../lib/format.js';
import { useAuth } from '../../lib/auth.jsx';
import { ArrowLeftRight } from 'lucide-react';
import { PageHero } from '../../components/PageHero.jsx';

/** §13: "Filter by free text, status, date range and amount; paginated." */
export function TransactionsPage() {
  const { merchant } = useAuth();
  const currency = merchant?.defaultCurrency ?? 'USD';

  /**
   * Seeded from the URL, so the global search box actually lands somewhere.
   *
   * It navigated here with `?search=…` and nothing read it — the box looked
   * broken because it was: you typed, the page changed, and the results did not.
   */
  const [params] = useSearchParams();
  const [filters, setFilters] = useState(() => ({
    search: params.get('search') ?? '',
    status: '', from: '', to: '', minAmount: '', maxAmount: '',
  }));
  const [applied, setApplied] = useState({});
  const [skip, setSkip] = useState(0);

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = {};
      if (filters.search.trim()) next.search = filters.search.trim();
      if (filters.status) next.status = filters.status;
      if (filters.from) next.from = filters.from;
      // Through the end of that day: a bare date is its first millisecond, which left the day itself out.
      if (filters.to) next.to = `${filters.to}T23:59:59.999Z`;

      const min = toMinorUnits(filters.minAmount, currency);
      const max = toMinorUnits(filters.maxAmount, currency);
      if (min != null) next.minAmount = min;
      if (max != null) next.maxAmount = max;

      setApplied(next);
      setSkip(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters, currency]);

  const query = useResource('/dashboard/payments', { params: { limit: 25, skip, ...applied } });
  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const hasFilters = Object.keys(applied).length > 0;

  return (
    <>
      <PageHero chip="Workspace" icon={ArrowLeftRight} title="Transactions" blurb="Search and filter every payment on your account." />

      <Card className="mb-6">
        <CardHeader
          title="Filters"
          actions={
            hasFilters && (
              <Button size="sm" variant="ghost"
                onClick={() => setFilters({ search: '', status: '', from: '', to: '', minAmount: '', maxAmount: '' })}>
                Clear
              </Button>
            )
          }
        />
        <div className="grid gap-3 p-5 sm:grid-cols-2 xl:grid-cols-7">
          <Field label="Search" className="sm:col-span-2">
            {(props) => (
              <Input {...props} type="search" value={filters.search} onChange={set('search')}
                placeholder="ID, order, email, description" />
            )}
          </Field>
          <Field label="Status">
            {(props) => (
              <Select {...props} value={filters.status} onChange={set('status')}>
                {STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </Select>
            )}
          </Field>
          <Field label="From">
            {(props) => <Input {...props} type="date" value={filters.from} onChange={set('from')} />}
          </Field>
          <Field label="To">
            {(props) => <Input {...props} type="date" value={filters.to} min={filters.from || undefined} onChange={set('to')} />}
          </Field>
          <Field label={`Min (${currency})`}>
            {(props) => <Input {...props} inputMode="decimal" value={filters.minAmount} onChange={set('minAmount')} placeholder="0.00" />}
          </Field>
          <Field label={`Max (${currency})`}>
            {(props) => <Input {...props} inputMode="decimal" value={filters.maxAmount} onChange={set('maxAmount')} placeholder="0.00" />}
          </Field>
        </div>
      </Card>

      <Card>
        <CardHeader title="Results" blurb={query.data ? `${query.data.total} matching` : undefined} />
        <PaymentsTable
          query={query}
          onPageChange={setSkip}
          emptyState={
            <EmptyState
              title={hasFilters ? 'No transactions match those filters' : 'No transactions yet'}
              blurb={
                hasFilters
                  ? 'Try widening the date range or clearing the search term.'
                  : 'Once you take your first payment it will appear here, searchable by ID, order, customer and amount.'
              }
              action={hasFilters
                ? <Button variant="secondary" onClick={() => setFilters({ search: '', status: '', from: '', to: '', minAmount: '', maxAmount: '' })}>Clear filters</Button>
                : undefined}
            />
          }
        />
      </Card>
    </>
  );
}

export default TransactionsPage;
